// Type imports
import type React from 'react'
import type { NextPage, GetStaticProps } from 'next'
import type { LinkTypes, I18nextKey, ActionTypes } from '../types'
import type { RequiredNs as ComponentNs } from '../components/localized/types'

// External imports
import Image from '../components/NextImage'
import { useRouter } from 'next/router'
import { FAQPageJsonLd, NextSeo } from 'next-seo'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useTranslation, Trans } from 'react-i18next'

import styled from '@emotion/styled'

// Misc
import useScrollPassed from '../hooks/use-scrolled-passed'

import Header from '../components/localized/Header'
import Footer from '../components/localized/Footer'
import PlatformLogos from '../components/localized/PlatformLogos'
import Testimonials from '../components/localized/Testimonials'

import { FlexHeroSection, FlexItem as HeroFlexItem, Title as HeroTitle, Description as HeroDescription } from '../components/Hero'
import { Section, FlexContainer, Item, FlexItem, Title, Description } from '../components/Section'
import { CenteredSection, Title as CenteredTitle } from '../components/CenteredSection'
import ColoredBackground from '../components/ColoredBackground'

import PrimaryAction from '../components/PrimaryAction'
import SecondaryAction from '../components/SecondaryAction'
import InlineLink from '../components/InlineLink'
import Highlight, { HighlightColor } from '../components/Highlight'
import FeatureGrid from '../components/FeatureGrid'
import BottomCta from '../components/BottomCta'

import HeroImage from '../../public/assets/feature-images/en/addin-sidebar.png'
import WordLogo from '../../public/assets/platform-logos/word.svg'

import { defaultPageSpecificSEO, pageSpecificCtaLink } from '../constants'

/**
 * Load all the namespaces required for this page include all localized components
 */
type RequiredNs = 'word' | 'common' | ComponentNs
const namespaces: Record<RequiredNs, true> = { common: true, word: true }
export const getStaticProps: GetStaticProps = async ({ locale }) => ({
  props: {
    ...(await serverSideTranslations(locale ?? '', Object.keys(namespaces)))
  }
})

/**
 * Constants & Styles
 */

const HERO_BACKGROUND = 'yellow'

const HeroImageWrapper = styled.div`
  position: relative;
  margin: 0 auto;
`

const LogoWrapper = styled.div`
  position: absolute;
  bottom: -3%;
  right: -5%;
  max-width: 16%;
`

/**
 * Types for structure of page
 */

// did this manually for each key because it was a pain to sort out typescript issues
// but it would be better if we just type it so its just the prefix of the key needed
type WordKey = I18nextKey<typeof import ('../../public/locales/en/word.json')>
interface FlexSectionContent extends Record<'titleKey' | 'descriptionKey', WordKey> {
  img: { src: any } & Record<'altKey', WordKey>
  action?: LinkTypes & Record<'textKey', WordKey>
  color?: HighlightColor
}

/**
 * Mapping json keys for content structure
 */

const FEATURES: FlexSectionContent[] = [
  {

    titleKey: 'features.grammar.TITLE',
    descriptionKey: 'features.grammar.DESCRIPTION',
    img: {
      src: require('../../public/assets/feature-images/en/grammar-word.png'),
      altKey: 'features.grammar.WORD_GRAMMAR_IMG_ALT'
    },
    color: 'green'
  },
  {
    titleKey: 'features.thesaurus.TITLE',
    descriptionKey: 'features.thesaurus.DESCRIPTION',
    img: {
      src: require('../../public/assets/feature-images/en/thesaurus-word.png'),
      altKey: 'features.thesaurus.WORD_THESAURUS_IMG_ALT'
    },
    color: 'pink'
  },
  {
    titleKey: 'features.rewrite.TITLE',
    descriptionKey: 'features.rewrite.DESCRIPTION',
    action: { href: '/paraphrasing', textKey: 'features.rewrite.ACTION_TEXT' },
    img: {
      src: require('../../public/assets/feature-images/en/rewrite-word.png'),
      altKey: 'features.rewrite.WORD_REWRITE_IMG_ALT'
    },
    color: 'purple'
  }
]

const Word: NextPage = () => {
  const router = useRouter()

  const { t } = useTranslation('word')
  const { t: tCommon } = useTranslation('common')

  const ctaAction: ActionTypes = { ...t('CTA_BUTTON'), ...pageSpecificCtaLink(router.pathname, router.locale) }

  // Controls for the header to only go sticky after user passes the fold
  const { ref: heroCtaRef, scrolledPassed } = useScrollPassed()

  return (
    <>
      <NextSeo
        {...defaultPageSpecificSEO(router.pathname, router.locale)}
        {...t('SEO')}
      />
      <FAQPageJsonLd
        mainEntity={[
          { questionName: t('seoFaq.installation.QUESTION'), acceptedAnswerText: t('seoFaq.installation.ANSWER') },
          { questionName: t('seoFaq.pricing.QUESTION'), acceptedAnswerText: t('seoFaq.pricing.ANSWER') }
        ]}
      />

      {/* Above the fold */}
      <Header
        t={tCommon}
        action={ctaAction}
        data-sticky={scrolledPassed}
        bgColor={HERO_BACKGROUND}
        dark
      />
      <ColoredBackground bgColor={HERO_BACKGROUND}>
        {/* Hero */}
        <FlexHeroSection>
          <HeroFlexItem>
            <HeroTitle>{t('hero.TITLE')}</HeroTitle>
            <HeroDescription>{t('hero.DESCRIPTION')}</HeroDescription>
            <PrimaryAction {...ctaAction} ref={heroCtaRef} clickId='hero-cta' />
          </HeroFlexItem>
          <HeroFlexItem>
            <HeroImageWrapper>
              <Image src={HeroImage} alt='' priority />
              <LogoWrapper>
                <Image src={WordLogo} alt='' priority />
              </LogoWrapper>
            </HeroImageWrapper>
          </HeroFlexItem>
        </FlexHeroSection>
      </ColoredBackground>

      {/* Features */}
      <Section bgColor='gray'>
        {FEATURES.map(({ titleKey, descriptionKey, img, action, color }, i) => {
          return (
          <FlexContainer key={titleKey} direction={i % 2 ? 'row-reverse' : 'row'}>
            <Item>
              <Image src={img.src} alt={t(img.altKey) as string} height={345} width={483} />
            </Item>
            <FlexItem>
              <div>
                <Title>
                  <Trans
                    t={t}
                    i18nKey={titleKey}
                    components={[<Highlight key={i} color={color} />]}
                  />
                </Title>
                <Description>{t(descriptionKey)}</Description>
              </div>
              {action && <SecondaryAction {...action} text={t(action.textKey) as string} />}
            </FlexItem>
          </FlexContainer>
          )
        })}
      </Section>

      {/* Platforms */}
      <CenteredSection>
        <CenteredTitle>
          <Trans t={t} i18nKey='platforms.TITLE'><Highlight color='yellow'/></Trans>
        </CenteredTitle>
        <PlatformLogos t={tCommon} platforms={['google-docs', 'chrome', 'edge', 'web-app']} />
      </CenteredSection>

      {/* FAQ */}
      <CenteredSection bgColor='gray'>
        <CenteredTitle>
          <Trans t={t} i18nKey={'faq.TITLE'}><Highlight color='green'/></Trans>
        </CenteredTitle>
        <FeatureGrid
          cols={2}
          descVisibleOnMobile
          list={[
            {
              title: t('faq.installation.QUESTION'),
              description: (
                <Trans t={t} i18nKey='faq.installation.ANSWER'>
                  <InlineLink exthref='https://support.outwrite.com/article/62-word-add-in' />
                </Trans>
              )
            },
            {
              title: t('faq.pricing.QUESTION'),
              description: t('faq.pricing.ANSWER')
            },
            {
              title: t('faq.usage.QUESTION'),
              description: t('faq.usage.ANSWER')
            },
            {
              title: t('faq.rewrite.QUESTION'),
              description: t('faq.rewrite.ANSWER')
            }
          ]}
        />
      </CenteredSection>

      {/* Testimonials */}
      <Testimonials t={tCommon} />

      {/* BottomCta */}
      <BottomCta action={ctaAction} {...t('BOTTOM_CTA')} />

      {/* Footer */}
      <Footer t={tCommon} />
    </>
  )
}

export default Word
